import { type FC, useMemo } from 'react';

import {
  boundariedValueDefault,
  boundariedValueForShowValue,
  calculateProgress,
} from '@/modules/progressBar';

import { ScProgressBar, type ScProgressBarProps } from './styled';

export enum ValuePosition {
  RIGHT = 'right',
  TOP = 'top',
  CENTER = 'center',
}

export enum Units {
  DEFAULT = '%',
  CP = ' CP',
  LP = ' LP',
}

const MIN_PROGRESS_COMPLETE = 3;

export interface ProgressBarProps extends ScProgressBarProps {
  from?: number;
  to?: number;
  units?: Units;
  showValue?: ValuePosition;
  showTo?: boolean;
  withUnits?: boolean;
  withDot?: boolean;
  isFloorRoundedValue?: boolean;
  completedText?: string;
  minProgress?: number;
}

export const ProgressBar: FC<ProgressBarProps> = ({
  value,
  units = Units.DEFAULT,
  from = 0,
  to = 100,
  withUnits,
  withDot = true,
  showValue,
  showTo = true,
  isFloorRoundedValue,
  completedText,
  minProgress,
  ...props
}) => {
  const progress = calculateProgress(value, from, to);
  const boundariedValue =
    showValue && showValue !== ValuePosition.CENTER
      ? boundariedValueForShowValue(progress)
      : boundariedValueDefault(progress);

  const minProgressValue = minProgress ?? MIN_PROGRESS_COMPLETE;

  const currentProgress: React.ReactElement = useMemo(
    () => (
      <>
        <p className="current-progress">
          {isFloorRoundedValue ? Math.floor(value) : value}
          {units}
        </p>
        {withDot && <div className="dot" />}
      </>
    ),
    [value, units, withDot, isFloorRoundedValue]
  );

  const isCompletedProgress = useMemo(() => value >= to, [to, value]);

  return (
    <ScProgressBar
      value={
        boundariedValue < minProgressValue ? minProgressValue : boundariedValue
      }
      valuePosition={showValue}
      {...props}
    >
      <div className="progress-bar-line">
        {showValue === ValuePosition.TOP && currentProgress}
        {showValue === ValuePosition.CENTER && (
          <span className="progress-value">
            {isCompletedProgress && completedText ? (
              completedText
            ) : (
              <>
                {value}
                {showTo && `/${to}`}
                {withUnits && units}
              </>
            )}
          </span>
        )}
        <div className="completed" />
      </div>
      {showValue === ValuePosition.RIGHT && currentProgress}
    </ScProgressBar>
  );
};
